import { useEffect, useState } from "react";
import IconButton from "../IconButton/IconButton";
import Search from "../Input/Search";
import GarnishList from "./GarnishList";
import { useTranslation } from "react-i18next";
import useOnScreenHook from "@/shared/hooks/useOnScreenHook";
import { Garnish, Topping } from "@/shared/utils/interfaces";
import { includesSearch } from "@/shared/utils/helpers";
import { UseQueryResult } from "@tanstack/react-query";

const itemCount = 10;

export interface SelectOptions {
  label: string;
  value: number;
}

interface GarnishesProps {
  setTabIndex: any;
  garnishQuery: UseQueryResult<Garnish[], Error>;
  toppingQuery: UseQueryResult<Topping[], Error>;
}

const Garnishes = ({
  setTabIndex,
  garnishQuery,
  toppingQuery,
}: GarnishesProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [create, setCreate] = useState(false);
  const { data: garnishes, refetch, isFetching } = garnishQuery;
  const { data: toppings } = toppingQuery;

  const [containerRef, isVisible] = useOnScreenHook({});
  const [pageCount, setPageCount] = useState(0);
  const [showArr, setShowArr] = useState(
    garnishes?.slice(itemCount * pageCount, itemCount * (pageCount + 1))
  );

  useEffect(() => {
    setShowArr(garnishes?.slice(0, itemCount * (pageCount + 1)));
  }, [garnishes]);

  useEffect(() => {
    if (isVisible) {
      const newPageCount = pageCount + 1;
      const newArr = garnishes?.slice(
        itemCount * newPageCount,
        itemCount * (newPageCount + 1)
      );
      setPageCount(newPageCount);
      setShowArr((prev) => [...prev, ...newArr]);
    }
  }, [isVisible]);

  return (
    <div className="mt-16">
      <div className="flex">
        <Search
          onDeleteClick={() => setSearch("")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("menu.garnishSearch")}
        />

        <div className="ml-[2rem]">
          <IconButton
            onClick={() => setCreate(!create)}
            variants={create ? "times" : "plus"}
            variant={create ? "danger" : "success"}
          />
        </div>
      </div>
      {create && (
        <div className="pb-8 mt-8 border-b border-b-gray-200">
          <div className="font-semibold text-2xl">
            {t("menu.garnishCreateMessage")}
          </div>
          <div className="overflow-x-auto">
            <GarnishList
              garnish={{
                code: "",
                name: { all: "" },
                is_active: true,
                topping_content_type: 1,
                item_type: "3",
              }}
              refetch={() => refetch()}
              setCreate={setCreate}
              toppings={toppings}
            />
          </div>
        </div>
      )}

      <div className="max-h-168 overflow-x-auto overflow-y-auto mt-8">
        {search !== ""
          ? garnishes
              ?.filter((g) => includesSearch(g, search))
              ?.map((garnish) => (
                <GarnishList
                  garnish={garnish}
                  key={garnish?.item_id}
                  toppings={toppings}
                  setTabIndex={setTabIndex}
                  refetch={() => refetch()}
                  isFetching={isFetching}
                />
              ))
          : showArr?.map((garnish, index) => (
              <div key={garnish?.item_id}>
                <GarnishList
                  garnish={garnish}
                  toppings={toppings}
                  setTabIndex={setTabIndex}
                  refetch={() => refetch()}
                  isFetching={isFetching}
                />
                {showArr.length - 1 === index &&
                  garnishes?.length - 1 !== index && (
                    // @ts-ignore
                    <div ref={containerRef} className="h-[50px]">
                      {t("menu.loading")}
                    </div>
                  )}
              </div>
            ))}
      </div>
    </div>
  );
};

export default Garnishes;
